import { Link } from "react-router-dom";
import { ShieldCheck, Database, RefreshCcw } from "lucide-react";
import { GlobalSearch } from "@/components/GlobalSearch";
import { useMfMeta, useTicker, navDate, num, pct } from "@/lib/format";

const QUICK = [["Large Cap", "Large Cap Fund"], ["Small Cap", "Small Cap Fund"], ["ELSS Tax Saver", "ELSS"], ["Index Funds", "Index Funds"]];

export const Hero = () => {
  const { data: m } = useMfMeta();
  const stats = [
    { icon: Database, v: m ? m.total_schemes.toLocaleString("en-IN") : "—", l: "Growth schemes tracked live", id: "schemes" },
    { icon: ShieldCheck, v: m?.amcs ?? "—", l: "AMCs registered with AMFI", id: "amcs" },
    { icon: RefreshCcw, v: navDate(m?.nav_date), l: "Latest official NAV date", id: "navdate" },
  ];
  return (
    <section data-testid="hero-section" className="relative z-20 grain overflow-x-clip bg-[#1E293B] text-white">
      <div className="absolute -right-40 -top-40 w-[600px] h-[600px] rounded-full bg-amber-400/10 blur-3xl" />
      <div className="absolute -left-20 bottom-0 w-[400px] h-[400px] rounded-full bg-emerald-400/10 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-8">
        <p className="rise inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-300/10 px-3 py-1 text-xs font-bold tracking-wider text-amber-200 uppercase">India's investment comparison desk</p>
        <h1 className="rise font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mt-5 max-w-4xl" style={{ animationDelay: "60ms" }}>
          Compare <span className="gold-text">funds, insurance, brokers & property</span> across India — on live data.
        </h1>
        <p className="rise mt-5 text-slate-300 text-base md:text-lg max-w-2xl" style={{ animationDelay: "120ms" }}>
          Official AMFI NAVs, live NSE/BSE, forex, gold & crypto prices, and location-based real estate. No tips, no bias — just the numbers you need to decide.
        </p>
        <div className="rise mt-8" style={{ animationDelay: "180ms" }}><GlobalSearch /></div>
        <div className="mt-4 flex flex-wrap gap-2">
          {QUICK.map(([l, c]) => <Link key={c} to={`/mutual-funds?category=${encodeURIComponent(c)}`} data-testid={`hero-quick-${l.toLowerCase().replace(/\s/g, "-")}`} className="rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-white/10 transition-colors">{l}</Link>)}
        </div>
        </div>
        <HeroPanel />
        </div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden max-w-4xl">
          {stats.map(({ icon: I, v, l, id }) => (
            <div key={id} className="bg-[#1E293B] p-5 flex items-center gap-4">
              <I className="w-6 h-6 text-amber-300 shrink-0" />
              <div><p data-testid={`hero-stat-${id}`} className="font-display text-2xl font-bold">{v}</p><p className="text-xs text-slate-400">{l}</p></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const HeroPanel = () => {
  const { data } = useTicker();
  const rows = (data?.indices || []).slice(0, 5);
  return (
    <div data-testid="hero-live-panel" className="hidden lg:block lg:col-span-4 rise rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl p-6 shadow-2xl" style={{ animationDelay: "240ms" }}>
      <div className="flex items-center justify-between">
        <p className="font-display font-bold">Live market snapshot</p>
        <span className="text-[10px] font-bold tracking-wider text-emerald-400 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-2 py-0.5">LIVE</span>
      </div>
      <div className="mt-5 divide-y divide-white/10">
        {rows.length === 0 && <p className="text-sm text-slate-400 py-4">Loading…</p>}
        {rows.map((q) => (
          <div key={q.symbol} className="flex items-center justify-between py-3">
            <span className="text-sm text-slate-300">{q.label}</span>
            <span className="text-right font-mono"><span className="block text-white font-semibold">{num(q.price)}</span><span className={`text-xs ${q.change_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>{pct(q.change_pct)}</span></span>
          </div>
        ))}
      </div>
    </div>
  );
};
